/**
 * PRE-283 — the question bank's pipeline, end to end, on one screen.
 *
 * PRE-256 built the line a stage at a time and each stage got its own place in
 * the panel: Questions for generation, Review queue for the human gate, Bank
 * health for what the answers say afterwards. The line itself had none, so
 * "where is the bank stuck today" was a question you answered by opening four
 * screens and holding the shape in your head.
 *
 * Modelled on User flow deliberately — same reading order, diagram then table —
 * but it is NOT that document and must not drift into it. User flow renders a
 * product CLAIM the owner writes (`ready` / `todo` / `gap`) and its statuses are
 * nobody's but theirs. This renders a MEASUREMENT: what the bank contains right
 * now, from `GET /v1/admin/question-flow`. Nothing here is authored, and nothing
 * here writes to the blueprint tables.
 *
 * It also decides nothing. Every step links to the screen that owns its
 * decision; approving, rejecting and retiring stay there. A view that both
 * diagnoses and acts is a view where a glance turns into a decision.
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowRight, IconInfoCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

import { Mermaid } from '../components/Mermaid';
import { useQuestionFlow } from '../hooks/use-content';
import { useReasonLabel, useT, type TranslateFn } from '../i18n';
import type { QuestionFlowReport, QuestionFlowStep, QuestionFlowStepId } from '../lib/api';
import { errorMessage } from '../lib/ui';

const STEP_LABEL = (report: QuestionFlowReport, id: QuestionFlowStepId) =>
  report.steps.find((s) => s.id === id)?.title ?? id;

/**
 * The line as a diagram. Mermaid node ids are the step ids, so the bottleneck
 * highlight below keys on exactly what the server said rather than on position.
 */
function flowChart(report: QuestionFlowReport, t: TranslateFn): string {
  const label = (s: QuestionFlowStep) => {
    const waiting =
      s.waiting != null && s.waiting > 0 ? `<br/>${t('flow.waiting', { count: s.waiting })}` : '';
    return `${s.id}["${s.title}${waiting}"]`;
  };
  const nodes = report.steps.map(label);
  const edges = report.steps.slice(1).map((s, i) => `${report.steps[i]!.id} --> ${s.id}`);
  // Rejections leave the line rather than continuing down it; drawing them as a
  // sink is the point of the picture — three of the ten steps can end an item.
  const rejects = ['invariant_gate', 'judge', 'human_review'].map((id) => `${id} -.-> discarded`);
  const highlight = report.bottleneck
    ? [
        'classDef bottleneck fill:#ffe3e3,stroke:#e03131,stroke-width:2px,color:#000',
        `class ${report.bottleneck} bottleneck`,
      ]
    : [];
  return [
    'flowchart LR',
    ...nodes.map((n) => `  ${n}`),
    `  discarded["${t('flow.discardedNode')}"]`,
    ...edges.map((e) => `  ${e}`),
    ...rejects.map((e) => `  ${e}`),
    ...highlight.map((h) => `  ${h}`),
  ].join('\n');
}

function StepCard({ step, isBottleneck }: { step: QuestionFlowStep; isBottleneck: boolean }) {
  const t = useT();
  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      h="100%"
      bd={isBottleneck ? '2px solid red.5' : undefined}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start" mb="xs">
        <Text fw={800}>{step.title}</Text>
        <Badge variant="light" color="gray" radius="sm" size="xs">
          {step.issue}
        </Badge>
      </Group>
      <Text size="sm" c="dimmed" mb="sm">
        {step.what}
      </Text>

      <Group gap="xs" mb="sm">
        {step.waiting != null ? (
          <Badge
            color={isBottleneck ? 'red' : step.waiting > 0 ? 'yellow' : 'gray'}
            variant={step.waiting > 0 ? 'filled' : 'light'}
            radius="sm"
          >
            {t('flow.waiting', { count: step.waiting })}
          </Badge>
        ) : null}
        {step.rejected != null ? (
          <Badge color="gray" variant="outline" radius="sm">
            {t('flow.discardedCount', { count: step.rejected })}
          </Badge>
        ) : null}
      </Group>

      <Stack gap={4} mb="sm">
        {step.metrics.map((m) => (
          <Group key={m.label} justify="space-between" gap="xs" wrap="nowrap">
            <Text size="xs" c="dimmed">
              {m.label}
            </Text>
            <Text size="sm" fw={600} ff="monospace">
              {m.of != null ? `${m.value} / ${m.of}` : m.value}
            </Text>
          </Group>
        ))}
      </Stack>

      {step.screen ? (
        <Button
          component={Link}
          to={step.screen}
          variant="subtle"
          size="compact-sm"
          rightSection={<IconArrowRight size={14} />}
        >
          {t('flow.goToScreen')}
        </Button>
      ) : (
        <Text size="xs" c="dimmed" fs="italic">
          {t('flow.noScreen')}
        </Text>
      )}
    </Card>
  );
}

export function QuestionFlowScreen() {
  const t = useT();
  const reasonLabel = useReasonLabel();
  const { data, isPending, error } = useQuestionFlow();

  if (isPending) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (error || !data) {
    return (
      <Alert color="red" title={t('flow.loadFailed')}>
        {errorMessage(error)}
      </Alert>
    );
  }

  const bottleneck = data.bottleneck ? data.steps.find((s) => s.id === data.bottleneck) : null;

  return (
    <Stack gap="xl">
      <Text c="dimmed" size="sm">
        {t('flow.intro')}
      </Text>

      <Group gap="lg" wrap="wrap">
        <Text size="sm">
          <b>{t('flow.published')}</b> {data.totals.published}
        </Text>
        <Text size="sm">
          <b>{t('flow.generated')}</b> {data.totals.generated}
        </Text>
        <Text size="sm">
          <b>{t('flow.rejected')}</b> {data.totals.rejected}
        </Text>
      </Group>

      {bottleneck ? (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title={t('flow.bottleneck')}
        >
          {t('flow.bottleneckBody', {
            step: bottleneck.title,
            count: bottleneck.waiting ?? 0,
            where: bottleneck.screen ? t('flow.bottleneckWhere') : '',
          })}
        </Alert>
      ) : (
        <Alert color="teal" variant="light" icon={<IconInfoCircle size={18} />}>
          {t('flow.noBottleneck')}
        </Alert>
      )}

      <div>
        <Title order={3} mb="xs">
          {t('flow.section1')}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('flow.section1Hint')}
        </Text>
        <Card withBorder padding="md" radius="md">
          <Mermaid chart={flowChart(data, t)} />
        </Card>
      </div>

      <div>
        <Title order={3} mb="xs">
          {t('flow.section2')}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('flow.section2Hint')}
        </Text>
        <Grid gap="sm">
          {data.steps.map((step) => (
            <Grid.Col key={step.id} span={{ base: 12, sm: 6, lg: 4 }}>
              <StepCard step={step} isBottleneck={step.id === data.bottleneck} />
            </Grid.Col>
          ))}
        </Grid>
      </div>

      <div>
        <Title order={3} mb="xs">
          {t('flow.section3')}
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          {t('flow.section3Hint')}
        </Text>
        {data.rejections.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t('flow.noRejections')}
          </Text>
        ) : (
          <Card withBorder padding={0} radius="md">
            <Table.ScrollContainer minWidth={520}>
              <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('flow.colReason')}</Table.Th>
                    <Table.Th w={200}>{t('flow.colStep')}</Table.Th>
                    <Table.Th w={90}>{t('flow.colCount')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.rejections.map((r) => (
                    <Table.Tr key={r.reason}>
                      <Table.Td fw={600}>{reasonLabel(r.reason)}</Table.Td>
                      <Table.Td c="dimmed">{STEP_LABEL(data, r.step)}</Table.Td>
                      <Table.Td ff="monospace">{r.count}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        )}
        <Alert mt="md" color="yellow" variant="light" icon={<IconInfoCircle size={18} />}>
          {t('flow.badlyWordedNote')}
        </Alert>
      </div>

      <Alert
        color="indigo"
        variant="light"
        icon={<IconInfoCircle size={18} />}
        title={t('flow.decidesNothing')}
      >
        {t('flow.decidesNothingBody')}
      </Alert>
    </Stack>
  );
}
